import Button from '@v2/shared/components/Button';
import Currency from '@v2/shared/components/Currency';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldCheck } from '@fortawesome/pro-regular-svg-icons';
import { Sku } from '@v2/shared/lib/types';
import { cn } from '@v2/shared/lib/utils';
import blackFridayExclusive from '../images/black-friday-exclusive.svg';

const BLACK_FRIDAY_OFFER_CODE = 'MGBLACKFRIDAY';

const BILLING_FREQUENCY: Record<string, string> = {
  '1': 'billed monthly',
  '12': 'billed annually',
  '24': 'billed every 2 years',
  '36': 'billed every 3 years',
};

type Props = {
  sku: Sku;
  oneMonthSku?: Sku;
  currency: string;
  offerCode: string | null;
  saleEndsOn: string;
  isAdding: boolean;
  onChoose: () => void;
};

function savingsPercentage(basePrice: number, discountedPrice: number): number {
  return Math.floor(-((discountedPrice / basePrice) * 100 - 100));
}

/**
 * Always UTC, never the reader's zone.
 *
 * The markup this appears in is server-rendered once and cached for a day, so the date
 * has to be the same wherever it is computed. Left to the local zone, the server and
 * the browser disagree for part of every day and hydration discards the render.
 */
function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { timeZone: 'UTC' });
}

/**
 * One plan, and the button that puts it in the cart.
 *
 * `sku` arrives already joined against the offer code — see PricingPanel — so
 * `sale_price` being set is what "on sale" means here.
 */
export default function PlanCard({ sku, oneMonthSku, currency, offerCode, saleEndsOn, isAdding, onChoose }: Props) {
  const price = sku.sale_price || sku.price;
  const months = Number(sku.attributes?.months ?? 1) || 1;
  const isYearlySubscription = months > 1;
  const monthlyPrice = price / months;

  const title = (sku.variants[0]?.content ?? sku.text)
    .replace(' Subscription', '')
    .replace('2 Year', '2 Years')
    .replace('3 Year', '3 Years');

  // On sale, the saving is against this plan's own list price. Otherwise it is against
  // twelve months of the monthly plan, which is what makes a full-price annual plan
  // still read as a saving.
  const savings = sku.sale_price
    ? savingsPercentage(sku.price, sku.sale_price)
    : oneMonthSku
      ? savingsPercentage(oneMonthSku.price, monthlyPrice)
      : 0;
  const savingsVerbiage = sku.sale_price ? `You save ${savings}` : `Save ${savings}`;

  const onSale = !!sku.sale_price && savings > 0;
  const showBlackFridayExclusive = !!sku.sale_price && offerCode === BLACK_FRIDAY_OFFER_CODE;

  // The offer's own end date when it has one, less a day because `valid_until` is the
  // first moment the price is gone rather than the last day it applies.
  const validThrough = sku.valid_until
    ? formatDate(new Date(Date.parse(sku.valid_until) - 24 * 60 * 60 * 1000))
    : formatDate(new Date(saleEndsOn));

  return (
    <div className="col-span-1 flex flex-col bg-white rounded-lg border-2 border-slate-900">
      {onSale ? (
        <div className="text-xs text-center font-bold bg-yellow-400 rounded-t-md p-1">
          Sale Valid Through {validThrough}
        </div>
      ) : (
        // Keeps the card bodies aligned across the row when only some are on sale.
        <div className="text-xs text-center font-bold p-1">&nbsp;</div>
      )}
      <div className="p-5">
        <div className="flex flex-col">
          <div className="text-xl font-bold mb-0 md:mb-4">{title}</div>
          <div className="text-slate-600 mb-3 md:mb-5">Unlimited access to 10,000+ magazines and newspapers</div>
          <div className={cn('h-auto mb-4 md:mb-8', showBlackFridayExclusive ? 'md:h-36' : 'md:h-24')}>
            <div className="flex flex-row items-end">
              <div>
                <span className={cn('font-bold text-3xl tracking-tight', onSale && 'text-red-600')}>
                  {showBlackFridayExclusive && isYearlySubscription && (
                    <div>
                      <img src={blackFridayExclusive} alt="Black Friday Exclusive" />
                    </div>
                  )}
                  <Currency value={price} currencyCode={currency} />
                  {onSale && (
                    <span className="ml-2">
                      <span className="font-semibold text-slate-600 line-through text-xl tracking-tight">
                        <Currency value={sku.price} currencyCode={currency} />
                      </span>
                    </span>
                  )}
                </span>
                <span className="text-lg font-medium text-slate-500 ml-1 tracking-normal">
                  {isYearlySubscription ? '' : '/mo'}
                </span>
              </div>
            </div>
            {/* headless showed this for any multi-month plan, having dereferenced the
                monthly sku unconditionally to work out the figure. Requiring a figure
                to show costs nothing when there is one and prints no "Save 0%!" when
                the monthly plan has gone away. */}
            {savings > 0 && (isYearlySubscription || onSale) && (
              <div className="font-semibold text-emerald-500 text-xl mb-3 md:mb-5">{savingsVerbiage}%!</div>
            )}
            <div className="text-sm font-medium text-slate-500 mb-0 md:mb-5">
              {isYearlySubscription && (
                <span className="text-sm font-medium text-slate-500 mb-0 md:mb-5">
                  <Currency value={monthlyPrice} currencyCode={currency} />
                  /month,{' '}
                </span>
              )}
              {BILLING_FREQUENCY[String(months)]}
            </div>
          </div>
        </div>
        <div>
          <Button
            color="yellow-400"
            onClick={onChoose}
            isLoading={isAdding}
            loadingText="Adding..."
            fullWidth
            ariaLabel={`Choose the ${title} plan`}
          >
            Choose
          </Button>
          <div className="text-slate-800 font-medium text-xs text-center mt-3">
            <FontAwesomeIcon icon={faShieldCheck} className="mr-2" />
            7-Day No Questions Asked Refund Guarantee
          </div>
        </div>
      </div>
    </div>
  );
}
